import { useMemo } from 'react'
import {
  CheckCircle2,
  Handshake,
  MessageSquare,
  Send,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react'

import { cn } from 'components/UI/cn'
import type { Campaign } from 'models/campaign'
import { WORKFLOW_STATUS } from 'models/campaign'
import {
  useGetCampaignReplies,
  useGetCampaignSequence,
  type Reply,
  type Sequence,
} from 'features/campaigns/sequences_api'
import { useGetCampaignInsights } from 'features/campaigns/campaign_insights_api'
import { formatInteger } from 'utils/formatting'

interface CampaignDashboardOverviewTabProps {
  campaign: Campaign
  campaignId: number
  campaignStatus: string
  onTabChange?: (tab: string) => void
  onViewSequence?: () => void
}

const SENTIMENT_CONFIG = {
  positive: {
    label: 'Interested',
    color: 'bg-brand/10 text-brand',
  },
  negative: {
    label: 'Not Interested',
    color: 'bg-red-100 text-red-800',
  },
  neutral: {
    label: 'Neutral',
    color: 'bg-surface-alt text-fg-secondary',
  },
} as const

function getSentiment(reply: Reply): keyof typeof SENTIMENT_CONFIG {
  if (reply.is_interested) return 'positive'
  return 'neutral'
}

import { formatRelativeTime } from 'utils/formatting'

function getReplyPreview(reply: Reply): string {
  if (reply.text_body) {
    return (
      reply.text_body.slice(0, 100) +
      (reply.text_body.length > 100 ? '...' : '')
    )
  }
  if (reply.html_body) {
    const stripped = reply.html_body.replace(/<[^>]*>/g, '')
    return stripped.slice(0, 100) + (stripped.length > 100 ? '...' : '')
  }
  return reply.subject || 'No preview available'
}

function toNumber(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

export function CampaignDashboardOverviewTab({
  campaign,
  campaignId,
  campaignStatus,
  onTabChange,
  onViewSequence,
}: CampaignDashboardOverviewTabProps) {
  const isPaused =
    campaignStatus === 'paused' ||
    campaignStatus === 'stopped' ||
    campaign.workflow_status === WORKFLOW_STATUS.PAUSED
  const isComplete =
    campaignStatus === 'completed' ||
    campaignStatus === 'archived' ||
    campaignStatus === 'deleted' ||
    campaign.workflow_status === WORKFLOW_STATUS.COMPLETE

  const { data: sequence } = useGetCampaignSequence(campaignId, !!campaign)
  const { data: insights } = useGetCampaignInsights(campaignId, !!campaign)
  const bisonCampaignId = sequence?.bison_campaign_id ?? 0
  const { data: repliesData } = useGetCampaignReplies(bisonCampaignId)
  const recentReplies = useMemo(
    () => repliesData?.results.slice(0, 3) ?? [],
    [repliesData]
  )

  const stats = useMemo(
    () => ({
      targets: campaign.contact_company_count ?? 0,
      contacts: campaign.contact_count ?? 0,
      deals: campaign.deal_count ?? 0,
      emailsSent: toNumber(insights?.summary.emails_sent),
      replyRate: toNumber(
        insights?.summary.unique_replies_per_contact_percentage
      ),
      interestedRate: toNumber(insights?.summary.interested_percentage),
    }),
    [campaign, insights]
  )

  const statCards = [
    {
      label: 'Targets',
      value: formatInteger(stats.targets) || '—',
      icon: Target,
    },
    {
      label: 'Contacts',
      value: formatInteger(stats.contacts) || '—',
      icon: Users,
    },
    {
      label: 'Deals',
      value: formatInteger(stats.deals) || '—',
      icon: Handshake,
    },
    {
      label: 'Emails Sent',
      value: stats.emailsSent > 0 ? formatInteger(stats.emailsSent) : '—',
      icon: Send,
    },
    {
      label: 'Reply Rate',
      value: stats.replyRate > 0 ? `${stats.replyRate.toFixed(1)}%` : '—',
      icon: MessageSquare,
    },
    {
      label: 'Interested Rate',
      value:
        stats.interestedRate > 0 ? `${stats.interestedRate.toFixed(1)}%` : '—',
      icon: TrendingUp,
    },
  ]

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-3 lg:grid-cols-6">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className="rounded-lg border border-line bg-surface p-3"
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm text-fg-secondary capitalize">
                {stat.label}
              </span>
              <stat.icon className="size-4 text-brand" />
            </div>
            <p className="text-base font-bold text-fg">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="rounded-lg border border-line bg-surface p-5">
          <div className="mb-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-base text-fg">Sequence Progress</h3>
              {!isComplete && (
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs',
                    isPaused
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-green-100 text-green-800'
                  )}
                >
                  {isPaused ? 'Paused' : 'Sending'}
                </span>
              )}
            </div>
            {onViewSequence && (
              <button
                type="button"
                onClick={onViewSequence}
                className="text-sm text-brand hover:underline"
              >
                View Sequence
              </button>
            )}
          </div>
          <SequenceProgressSection
            sequence={sequence}
            stepStats={insights?.summary.sequence_step_stats}
            isComplete={isComplete}
          />
        </div>

        <div className="rounded-lg border border-line bg-surface p-5">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="text-base text-fg">Recent Replies</h3>
            {onTabChange && (
              <button
                type="button"
                onClick={() => onTabChange('replies')}
                className="text-sm text-brand hover:underline"
              >
                View All Replies
              </button>
            )}
          </div>
          {recentReplies.length > 0 ? (
            <div className="divide-y divide-line/50 space-y-0">
              {recentReplies.map((reply) => {
                const sentiment = SENTIMENT_CONFIG[getSentiment(reply)]
                return (
                  <div
                    key={reply.id}
                    className="cursor-pointer py-3 first:pt-0 last:pb-0 hover:bg-surface-hover -mx-1 rounded px-1 transition-colors"
                    onClick={() => onTabChange?.('replies')}
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-sm text-fg">
                        {reply.from_name || reply.from_email_address}
                      </span>
                      <span className="text-xs text-fg-secondary">
                        {formatRelativeTime(reply.date_received)}
                      </span>
                    </div>
                    <p className="mb-1.5 truncate text-sm text-fg-secondary">
                      {getReplyPreview(reply)}
                    </p>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs',
                        sentiment.color
                      )}
                    >
                      {sentiment.label}
                    </span>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="py-8 text-center">
              <MessageSquare className="mx-auto mb-2 size-8 text-fg-muted" />
              <p className="text-sm text-fg-secondary">No replies yet</p>
            </div>
          )}
        </div>
      </div>
      {isComplete && (
        <div className="rounded-xl border border-brand/10 bg-gradient-to-r from-brand/5 to-emerald-50 p-6">
          <div className="flex items-center gap-4">
            <div className="flex size-12 items-center justify-center rounded-xl bg-brand/10">
              <CheckCircle2 className="size-6 text-brand" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-fg">
                Campaign Completed
              </h3>
              <p className="mt-0.5 text-sm text-fg-secondary">
                This campaign finished sending all configured sequence steps.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function SequenceProgressSection({
  sequence,
  stepStats,
  isComplete,
}: {
  sequence?: Sequence | null
  stepStats?:
    | Array<{
        sequence_step_id: number
        sent: number
        interested: number
        unique_replies: number
      }>
    | undefined
  isComplete: boolean
}) {
  if (!sequence?.steps?.length) {
    return (
      <div className="py-8 text-center">
        <Send className="mx-auto mb-2 size-8 text-fg-muted" />
        <p className="text-sm text-fg-secondary">No sequence steps yet</p>
      </div>
    )
  }

  let startedStepIndex = -1
  sequence.steps.forEach((step, index) => {
    const stat = stepStats?.find(
      (stepStat) => stepStat.sequence_step_id === step.id
    )
    if (!stat) return
    if (stat.sent > 0 || stat.interested > 0 || stat.unique_replies > 0) {
      startedStepIndex = index
    }
  })

  return (
    <div className="space-y-5">
      {sequence.steps.map((step, index) => {
        const stat = stepStats?.find(
          (stepStat) => stepStat.sequence_step_id === step.id
        )
        const totalSent = stat?.sent ?? 0
        const interested = stat?.interested ?? 0
        const replied = stat?.unique_replies ?? 0
        const hasStepActivity =
          totalSent > 0 || interested > 0 || replied > 0 || isComplete
        const progress = hasStepActivity
          ? 100
          : startedStepIndex >= 0 && index === startedStepIndex + 1
            ? 50
            : 0

        return (
          <div key={step.id}>
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-3 text-sm text-fg-secondary">
                  {index + 1}
                </span>
                <span className="max-w-[220px] truncate text-sm text-fg">
                  {step.email_subject || `Step ${index + 1}`}
                </span>
              </div>
              <span className="text-sm text-fg-secondary">{progress}%</span>
            </div>
            <div className="ml-5">
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-disabled">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    progress > 0 ? 'bg-brand-hover' : 'bg-surface-disabled'
                  )}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="mt-1.5 flex items-center gap-4">
                <span className="text-xs text-fg-muted">{totalSent} sent</span>
                <span className="text-xs text-fg-muted">
                  {interested} interested
                </span>
                <span className="text-xs text-fg-muted">{replied} replied</span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default CampaignDashboardOverviewTab
